import { useCallback, useState } from 'react';

import { Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Operation } from 'src/types/operation';
import { matchesCnpjSearch } from 'src/utils/document';
import { debounce } from 'src/utils/general';

type FundSearchSectionProps = {
  operations: Operation[];
  isLoadingOperations: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onFundSelect: (fund: Operation) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onInputFocus?: (focused: boolean) => void;
  restrictSearch?: boolean;
};

export function FundSearchSection({
  operations,
  isLoadingOperations,
  searchValue,
  onSearchChange,
  onFundSelect,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  onInputFocus,
  restrictSearch = false,
}: FundSearchSectionProps) {
  const [inputFocused, setInputFocused] = useState(false);

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      onSearchChange(value);
    }, 500),
    [onSearchChange],
  );

  const handleFocus = useCallback(() => {
    setInputFocused(true);
    onSearchChange('');
  }, [onSearchChange]);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Buscar e Adicionar Fundos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pesquise fundos por nome ou CNPJ para adicionar à sua carteira.
      </Typography>

      <Autocomplete
        freeSolo
        blurOnSelect
        fullWidth
        popupIcon={null}
        noOptionsText={<Box>Nenhum fundo encontrado</Box>}
        loading={isLoadingOperations || isFetchingNextPage}
        loadingText="Carregando fundos..."
        options={Array.isArray(operations) ? operations : []}
        filterOptions={(options, state) => {
          if (!restrictSearch) return options;

          const normalizedQuery = state.inputValue.trim().toLowerCase();
          if (!normalizedQuery) return options;

          return options.filter(
            (operation) =>
              operation.name.toLowerCase().includes(normalizedQuery) ||
              matchesCnpjSearch(operation.cnpj, state.inputValue),
          );
        }}
        renderOption={(props, option) => (
          <Box {...props} component="li" sx={{ display: 'flex', gap: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {option.name}
            </Typography>
            -
            <Typography variant="body2" color="text.secondary">
              {option.cnpj}
            </Typography>
          </Box>
        )}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.name || '';
        }}
        onInputChange={(_, value) => {
          debouncedSetSearch(value);
        }}
        onOpen={() => {
          if (!inputFocused) {
            handleFocus();
          }
        }}
        onChange={(_, value) => {
          if (value && typeof value === 'object') {
            onFundSelect(value);

            onSearchChange('');
          }
        }}
        value={null}
        ListboxProps={{
          onScroll: (event) => {
            if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;

            const listboxNode = event.currentTarget;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight >=
              listboxNode.scrollHeight - 10
            ) {
              onLoadMore();
            }
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Buscar fundos por nome ou CNPJ..."
            onFocus={handleFocus}
            onBlur={() => setInputFocused(false)}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
